import Phaser from 'phaser'
import { t } from '../../i18n'
import { audio } from '../JKGame_audio'
import { isBackendEnabled } from '../../services/JKGame_api'
import { apiAccountBattlepass, apiAccountSettle, apiDaily, apiSettleReplay, dailySeed, getHandle, missionLabel, todayISO, type SettleResult } from '../../services/JKGame_economy'
import { getMetaProgressSummary } from '../../services/JKGame_metaProgress'
import { matchController } from '../../services/JKGame_MatchController'
import { getActiveChar } from '../../services/JKGame_loadout'
import type { WinReason } from '../../sim/JKGame_types'
import { type Emotion, porKey, portraitSetFor } from '../JKGame_assets'
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../JKGame_dims'
import { COLORS, FONT, hasFinePointer, prefersReducedMotion } from '../JKGame_ui'
import { mountResultOverlay, type ResultOverlayHandle } from '../../ui/JKGame_resultOverlay'

export interface JKGame_ResultData {
  reason: WinReason
  rank: number
  total: number
  kills: number
  survived: number
}

export class JKGame_ResultScene extends Phaser.Scene {
  private alive = true // false after SHUTDOWN; guards the async settle callback
  private navigating = false // true once a leave (play-again / menu) has begun; blocks a double-fire
  private resultOverlay?: ResultOverlayHandle
  private readonly keyEnter = (): void => this.playAgain()
  private readonly keyEsc = (): void => this.toMenu()

  constructor() {
    super('Result')
  }

  create(data: JKGame_ResultData): void {
    const cx = DESIGN_WIDTH / 2
    this.alive = true
    this.navigating = false // reset on (re)entry — Phaser reuses the scene instance across restarts
    this.resultOverlay?.destroy()
    this.resultOverlay = undefined
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.resultOverlay?.destroy()
      this.resultOverlay = undefined
      this.input.keyboard?.off('keydown-ENTER', this.keyEnter)
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
    })
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(300, 0, 0, 0) // gentle result reveal; clears on scene change

    const won = data.reason === 'survivor' || data.reason === 'escape'
    audio.play(won ? 'success' : 'error') // outcome sting (once, on the result screen)

    // Deployed-character portrait — makes the player's chosen roster character (and its cosmetic
    // identity) VISIBLE at the match's emotional peak: a smile on a win, sad on a loss. This closes
    // the "you never see your own character" gap that made cosmetics worthless (see
    // docs/playability-monetization.md §2/§5). Texture-guarded so a missing portrait just no-ops.
    const avatarSet = portraitSetFor(getActiveChar())
    const emo: Emotion = won ? 'smile' : 'sad'
    const avatarKey = this.textures.exists(porKey(avatarSet, emo)) ? porKey(avatarSet, emo)
      : this.textures.exists(porKey(avatarSet, 'plane')) ? porKey(avatarSet, 'plane') : ''
    if (avatarKey) {
      const avatar = this.add.image(cx, DESIGN_HEIGHT * 0.135, avatarKey).setScale(0.62)
      if (won && !prefersReducedMotion()) {
        avatar.setScale(0.52)
        this.tweens.add({ targets: avatar, scale: 0.62, duration: 420, ease: 'Back.Out' })
      }
    }
    const titleKey = data.reason === 'escape' ? 'result.win.escape' : data.reason === 'survivor' ? 'result.win.survivor' : 'result.lose'
    const flavorKey = data.reason === 'escape' ? 'result.l-escape' : data.reason === 'survivor' ? 'result.l-survivor' : 'result.l-dead'

    const titleTxt = this.add.text(cx, DESIGN_HEIGHT * 0.26, t(titleKey), {
      fontFamily: FONT,
      fontSize: '52px',
      color: won ? COLORS.good : COLORS.bad,
      fontStyle: 'bold',
    }).setOrigin(0.5)
    // Shrink to fit so a long localized title never clips off the portrait edge.
    const maxTitleW = DESIGN_WIDTH - 48
    if (titleTxt.width > maxTitleW) titleTxt.setScale(maxTitleW / titleTxt.width)
    // A win earns a celebratory scale-pop (popping from the fitted scale so it never
    // overshoots the clip guard); a loss stays still and somber. Skipped under
    // prefers-reduced-motion (the color/text already convey the win). Tween dies w/ scene.
    if (won && !prefersReducedMotion()) {
      const rest = titleTxt.scale
      titleTxt.setScale(rest * 1.3)
      this.tweens.add({ targets: titleTxt, scale: rest, duration: 380, ease: 'Back.Out' })
    }

    const flavor = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT * 0.26 + 64, t(flavorKey), {
      fontFamily: FONT,
      fontSize: '19px',
      color: COLORS.sub,
      align: 'center',
      wordWrap: { width: DESIGN_WIDTH - 80, useAdvancedWrap: true },
    }).setOrigin(0.5)
    // Shrink an unusually long localized flavour so it never grows down into the rank line.
    const maxFlavorH = DESIGN_HEIGHT * 0.5 - 30 - (DESIGN_HEIGHT * 0.26 + 64) - 12
    if (flavor.height > maxFlavorH) flavor.setScale(maxFlavorH / flavor.height)

    // Placement-graded rank cue: champion gold, top quartile green, else neutral —
    // immediate "how did I place" feedback (cosmetic; identical in both modes).
    const topCut = Math.max(1, Math.ceil(data.total * 0.25))
    const rankColor = data.rank === 1 ? '#ffd84d' : data.rank <= topCut ? COLORS.good : COLORS.text
    // Champion gets a 🏆 (emoji, no new key) to crown the gold #1 line — pure delight.
    const rankText = (data.rank === 1 ? '🏆 ' : '') + t('result.rank', { rank: data.rank, total: data.total })
    const rankTxt = this.add.text(cx, DESIGN_HEIGHT * 0.5 - 30, rankText, {
      fontFamily: FONT,
      fontSize: '26px',
      color: rankColor,
      fontStyle: data.rank <= topCut ? 'bold' : 'normal',
      align: 'center',
    }).setOrigin(0.5)
    // Shrink a wide localized rank line (e.g. 🏆 + a 3-digit total) so it never clips the edges.
    const maxRankW = DESIGN_WIDTH - 48
    if (rankTxt.width > maxRankW) rankTxt.setScale(maxRankW / rankTxt.width)
    // Icon prefixes for at-a-glance scannability (emoji, no new key) — consistent with
    // the project's marker vocabulary (🔑/🏆/🩹/⚠): ⚔ kills, 👣 steps survived.
    const stats = [
      `⚔ ${t('result.kills', { n: data.kills })}`,
      `👣 ${t('result.survived', { n: data.survived })}`,
    ].join('\n')
    this.add.text(cx, DESIGN_HEIGHT * 0.5 + 24, stats, {
      fontFamily: FONT,
      fontSize: '22px',
      color: COLORS.text,
      align: 'center',
      lineSpacing: 12,
    }).setOrigin(0.5)

    let rewardText = t('result.rewards')
    // Authoritative settlement (backend flag only): coin + season XP + dailies.
    if (isBackendEnabled()) {
      rewardText = `${t('result.rewards')}\n${t('result.settling')}`
    } else {
      // Local-mode permanent progression: award souls (roguelite meta) once and show the gain.
      // Souls accrue in localStorage until the backend owns settlement (anti-cheat, P step).
      const award = matchController.settleLocalSouls()
      const meta = getMetaProgressSummary()
      const nextLine = meta.unlockableCount > 0
        ? t('result.nextUnlockReady', { n: meta.unlockableCount })
        : meta.missingForNext !== null
          ? t('result.nextUnlockSoon', { n: meta.missingForNext })
          : t('result.nextUnlockComplete')
      const rewardLine = award ? t('result.souls', { n: award.souls, balance: award.balance }) : t('result.rewards')
      rewardText = `${rewardLine}\n${nextLine}`
    }
    this.resultOverlay = mountResultOverlay({
      title: t('result.nextTitle'),
      rewardText,
      primaryLabel: t('result.again'),
      collectionLabel: t('result.toCollection'),
      passLabel: t('result.toPass'),
      menuLabel: t('result.toMenu'),
      onPlayAgain: () => this.playAgain(),
      onCollection: () => this.toScene('Collection'),
      onPass: () => this.toScene('Progress'),
      onMenu: () => this.toMenu(),
    })
    if (isBackendEnabled()) void this.settle(data, this.resultOverlay)

    // Keyboard nav (desktop): Enter = play again, Esc = main menu. Scene-scoped
    // listeners are cleaned up on shutdown.
    this.input.keyboard?.off('keydown-ENTER', this.keyEnter).on('keydown-ENTER', this.keyEnter)
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc)
    // Desktop-only hint for the keyboard shortcuts (hidden on touch), matching the
    // other scenes' discoverability hints.
    if (hasFinePointer()) {
      this.add.text(cx, DESIGN_HEIGHT - 8, t('result.kbd'), { fontFamily: FONT, fontSize: '12px', color: COLORS.sub }).setOrigin(0.5, 1)
    }
  }

  private playAgain(): void {
    // Guard against a double-fire (rapid double-Enter, or Enter then a tap): without it
    // startBackendMatch would spin up TWO server sessions and two scene.start('Explore')
    // calls would race. Once we're leaving, ignore further nav input from this screen.
    if (this.navigating) return
    this.navigating = true
    void this.launchPlayAgain()
  }

  private async launchPlayAgain(): Promise<void> {
    let seed = (Date.now() >>> 0) || 1
    if (matchController.isRanked()) {
      try {
        seed = (await apiDaily()).seed
      } catch {
        seed = dailySeed(todayISO())
      }
    }
    if (!this.alive) return
    matchController.newMatch(seed)
    void matchController.startBackendMatch(seed) // authoritative session (no-op unless flag on)
    this.scene.start('Explore')
  }

  /** Leave to the main menu, guarded so it can't race a play-again or a second menu nav. */
  private toMenu(): void {
    this.toScene('Menu')
  }

  private toScene(key: string): void {
    if (this.navigating) return
    this.navigating = true
    this.scene.start(key)
  }

  private async settle(data: JKGame_ResultData, overlay: ResultOverlayHandle): Promise<void> {
    const handle = getHandle()
    overlay.setRewardText(`${t('result.rewards')}\n${t('result.settling')}`) // pending feedback
    try {
      // Prefer A-方案 replay settlement: submit the seed + action sequence so the SERVER re-derives
      // the result (forge-proof). Falls back to the clamped report path only if no replay payload.
      const replay = matchController.getReplayPayload()
      const res: SettleResult = replay
        ? await apiSettleReplay(handle, replay)
        : await apiAccountSettle(handle, { rank: data.rank, kills: data.kills, steps: data.survived, total: data.total })
      if (!this.alive) return // navigated away (Play Again / Main Menu) mid-settle
      const lines = this.rewardLines(res)
      // If the season XP just unlocked BattlePass rewards, nudge the player.
      try {
        const bp = await apiAccountBattlepass(handle)
        if (bp.claimable.length > 0) lines.push(t('bp.hint', { n: bp.claimable.length }))
      } catch {
        // BP hint is best-effort; settlement already succeeded — stay quiet here.
      }
      if (!this.alive) return
      overlay.setRewardText(lines.join('\n'))
    } catch {
      if (!this.alive) return
      // Never silent: show a localized offline note instead of raw failure.
      overlay.setRewardText(`${t('result.rewards')}\n${t('result.settleOffline')}`)
    }
  }

  private rewardLines(res: SettleResult): string[] {
    // Icon prefixes (emoji, no new key) for at-a-glance scannability, consistent with the
    // 🪙 in the gacha balance and the ⚔/👣 result stats: 🪙 coin, ⭐ season XP.
    const lines = [
      t('result.rewards'),
      `🪙 ${t('result.gainCoin', { n: res.gained.coin })}`,
      `⭐ ${t('result.gainXp', { n: res.gained.xp })}`,
    ]
    // souls earned (backend-authoritative settlement) — mirrors the local-mode souls line so the
    // roguelite meta currency is shown in both paths. gained.souls may be absent on older servers.
    const soulsGained = res.gained.souls ?? res.souls
    if (soulsGained) lines.push(`🔮 ${t('result.souls', { n: soulsGained, balance: res.wallet.souls })}`)
    if (res.level_after > res.level_before) {
      lines.push(t('result.levelUp', { from: res.level_before, to: res.level_after }))
    } else {
      lines.push(t('result.seasonLv', { lv: res.season.level }))
    }
    for (const m of res.missions) {
      lines.push(t('result.missionDone', { name: missionLabel(m.id, m.title) }))
    }
    return lines
  }
}
